import { useMemo } from 'react'
import { useFinance } from '../context/FinanceContext'
import { useAuth } from '../context/AuthContext'
import { useSettings } from '../context/SettingsContext'
import { PAGES, getThemeColors } from '../lib/constants'
import GlassCard from '../components/ui/GlassCard'

// ── Calculator Registry ────────────────────────────────────────────
const CALCULATORS = [
  // Income & Career
  { key: 'paycheck', title: 'Paycheck Calculator', description: 'Estimate your take-home pay after taxes and deductions', page: PAGES.INCOME_CAREER, category: 'Income & Career', colorKey: 'primary', icon: DollarIcon },
  { key: 'bonusrsu', title: 'Bonus / RSU Tax', description: 'Withholding estimates for bonuses and RSU vesting', page: PAGES.INCOME_CAREER, category: 'Income & Career', colorKey: 'warning', icon: GiftIcon },
  { key: 'selfemployment', title: 'Self-Employment Tax', description: 'Estimate SE tax including Social Security and Medicare', page: PAGES.INCOME_CAREER, category: 'Income & Career', colorKey: 'negative', icon: BriefcaseIcon },
  { key: 'freelancer', title: 'Freelancer Rate Calculator', description: 'Find your required hourly rate to match W-2 take-home pay', page: PAGES.INCOME_CAREER, category: 'Income & Career', colorKey: 'secondary', icon: ClipboardIcon },
  { key: 'lifestylecreep', title: 'Lifestyle Creep Detector', description: 'See how much wealth you lost to rising expenses', page: PAGES.INCOME_CAREER, category: 'Income & Career', colorKey: 'positive', icon: CreepIcon },
  // Financial Health
  { key: 'emergency', title: 'Emergency Fund', description: 'Score-based calculator for your ideal emergency fund target', page: PAGES.FINANCIAL_HEALTH, category: 'Financial Health', colorKey: 'positive', icon: ShieldIcon },
  { key: 'savings', title: 'Savings Goal', description: 'Track progress toward any savings target with timeline projections', page: PAGES.FINANCIAL_HEALTH, category: 'Financial Health', colorKey: 'primary', icon: TargetIcon },
  { key: 'networth', title: 'Net Worth Tracker', description: 'Assets minus liabilities — see your full financial picture', page: PAGES.FINANCIAL_HEALTH, category: 'Financial Health', colorKey: 'secondary', icon: ChartIcon },
  { key: 'col', title: 'Cost of Living', description: 'Compare cost of living between cities side by side', page: PAGES.FINANCIAL_HEALTH, category: 'Financial Health', colorKey: 'warning', icon: MapIcon },
  // Loans & Debt
  { key: 'loans', title: 'Loan Calculator', description: 'Mortgage, auto, and personal loan calculators with amortization', page: PAGES.LOANS_DEBT, category: 'Loans & Debt', colorKey: 'primary', icon: LoanIcon },
  { key: 'compare', title: 'Compare Scenarios', description: 'Side-by-side comparison of different loan options', page: PAGES.LOANS_DEBT, category: 'Loans & Debt', colorKey: 'negative', icon: CompareIcon },
  { key: 'debt', title: 'Debt Payoff', description: 'Compare snowball vs avalanche strategies for your debts', page: PAGES.LOANS_DEBT, category: 'Loans & Debt', colorKey: 'warning', icon: DebtIcon },
  { key: 'debtvsinvest', title: 'Debt vs Invest', description: 'Should you pay off debt faster or invest the extra?', page: PAGES.LOANS_DEBT, category: 'Loans & Debt', colorKey: 'positive', icon: SwapIcon },
  // Life Decisions
  { key: 'car', title: 'Car Affordability', description: '20/3/8 rule check — can you really afford that car?', page: PAGES.LIFE_DECISIONS, category: 'Life Decisions', colorKey: 'primary', icon: CarIcon },
  { key: 'home', title: 'Home Affordability', description: 'DTI-based analysis of your max affordable home price', page: PAGES.LIFE_DECISIONS, category: 'Life Decisions', colorKey: 'secondary', icon: HomeIcon },
  { key: 'rentvsbuy', title: 'Rent vs Buy', description: 'Total cost comparison of renting versus buying over time', page: PAGES.LIFE_DECISIONS, category: 'Life Decisions', colorKey: 'positive', icon: RentBuyIcon },
  { key: 'baby', title: 'Baby Cost Estimator', description: 'Estimate first-year and ongoing costs for a new baby', page: PAGES.LIFE_DECISIONS, category: 'Life Decisions', colorKey: 'warning', icon: HeartIcon },
  // Investing & Retirement
  { key: 'investments', title: 'Investment Growth', description: 'Project future growth with compound interest modeling', page: PAGES.INVESTING_RETIREMENT, category: 'Investing & Retirement', colorKey: 'positive', icon: TrendUpIcon },
  { key: 'fire', title: 'FIRE Calculator', description: 'Financial independence — when can you retire early?', page: PAGES.INVESTING_RETIREMENT, category: 'Investing & Retirement', colorKey: 'warning', icon: FireIcon },
  { key: 'socialsecurity', title: 'Social Security Estimator', description: 'Compare claiming at 62, FRA, or 70 with break-even analysis', page: PAGES.INVESTING_RETIREMENT, category: 'Investing & Retirement', colorKey: 'primary', icon: SSIcon },
  { key: 'retirement', title: 'STRS / Pension', description: 'Estimate your pension benefit based on years of service', page: PAGES.INVESTING_RETIREMENT, category: 'Investing & Retirement', colorKey: 'secondary', icon: ClockIcon },
  { key: 'rothvstrad', title: 'Roth vs Traditional', description: 'Compare after-tax retirement outcomes for each account type', page: PAGES.INVESTING_RETIREMENT, category: 'Investing & Retirement', colorKey: 'primary', icon: CalcIcon },
  { key: 'capitalgains', title: 'Capital Gains Tax', description: 'Short-term and long-term capital gains tax estimates', page: PAGES.INVESTING_RETIREMENT, category: 'Investing & Retirement', colorKey: 'negative', icon: TrendingIcon },
]

const CATEGORY_ORDER = ['Income & Career', 'Financial Health', 'Loans & Debt', 'Life Decisions', 'Investing & Retirement']
const DEFAULT_FEATURED_KEYS = ['paycheck', 'emergency', 'fire']

export default function Dashboard() {
  const { state, navigate } = useFinance()
  const { user } = useAuth()
  const { settings, trackCalculatorOpen } = useSettings()
  const colors = getThemeColors(state.theme)

  // Derive display name
  const fullName = user?.displayName || (user?.email ? user.email.split('@')[0].charAt(0).toUpperCase() + user.email.split('@')[0].slice(1) : 'there')
  const displayName = fullName.split(' ')[0]

  // Featured calculators: top 3 by usage, backfilled with defaults
  const { featured, hasUsage } = useMemo(() => {
    const usage = settings.calculatorUsage || {}
    const usedKeys = Object.entries(usage)
      .filter(([, count]) => count > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([key]) => key)

    const hasUsage = usedKeys.length > 0
    const featuredKeys = [...usedKeys]
    for (const dk of DEFAULT_FEATURED_KEYS) {
      if (featuredKeys.length >= 3) break
      if (!featuredKeys.includes(dk)) featuredKeys.push(dk)
    }

    const featured = featuredKeys.slice(0, 3).map(key => CALCULATORS.find(c => c.key === key)).filter(Boolean)
    return { featured, hasUsage }
  }, [settings.calculatorUsage])

  // Group calculators by category
  const grouped = useMemo(() => {
    const map = {}
    for (const calc of CALCULATORS) {
      if (!map[calc.category]) map[calc.category] = []
      map[calc.category].push(calc)
    }
    return CATEGORY_ORDER.map(cat => ({ category: cat, items: map[cat] || [] }))
  }, [])

  const totalCalcs = CALCULATORS.length
  const totalCategories = CATEGORY_ORDER.length

  function handleCardClick(calc) {
    trackCalculatorOpen(calc.key)
    navigate(calc.page, calc.key)
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-8">
      {/* Hero Section */}
      <div
        className="relative overflow-hidden rounded-3xl p-6 sm:p-8"
        style={{
          background: `linear-gradient(135deg, ${colors.primary}18, ${colors.secondary}18)`,
          border: `1px solid ${colors.primary}25`,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
        }}
      >
        {/* Decorative gradient orbs */}
        <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full opacity-20" style={{ background: `radial-gradient(circle, ${colors.primary}, transparent)` }} />
        <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full opacity-15" style={{ background: `radial-gradient(circle, ${colors.secondary}, transparent)` }} />

        <div className="relative">
          <h1 className="text-2xl sm:text-3xl font-bold text-heading mb-2">
            Welcome back, <span style={{ color: colors.primary }}>{displayName}</span>
          </h1>
          <p className="text-muted text-sm sm:text-base mb-4">Your financial calculator toolkit</p>
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
            style={{
              background: `${colors.primary}18`,
              color: colors.primary,
              border: `1px solid ${colors.primary}30`,
            }}
          >
            <GridIcon />
            {totalCalcs} calculators across {totalCategories} categories
          </span>
        </div>
      </div>

      {/* Featured Cards Section */}
      <section>
        <h2 className="text-lg font-semibold text-heading mb-4">
          {hasUsage ? 'Recently Used' : 'Popular Calculators'}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {featured.map(calc => {
            const color = colors[calc.colorKey]
            const Icon = calc.icon
            return (
              <div
                key={calc.key}
                onClick={() => handleCardClick(calc)}
                className="glass-hover cursor-pointer p-5 group rounded-2xl"
                style={{ borderLeft: `3px solid ${color}` }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-transform duration-200 group-hover:scale-110"
                      style={{ backgroundColor: `${color}22`, color }}
                    >
                      <Icon />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-heading mb-1">{calc.title}</h3>
                      <p className="text-xs text-muted leading-relaxed mb-2">{calc.description}</p>
                      <span
                        className="inline-block px-2 py-0.5 rounded-full text-[10px] font-medium"
                        style={{ background: `${color}15`, color, border: `1px solid ${color}25` }}
                      >
                        {calc.category}
                      </span>
                    </div>
                  </div>
                  <svg className="w-4 h-4 text-faint group-hover:text-heading transition shrink-0 mt-1 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            )
          })}
        </div>
      </section>

      {/* All Calculators Grid — Grouped by Category */}
      <section>
        <div className="flex items-center gap-3 mb-6">
          <h2 className="text-lg font-semibold text-heading">All Calculators</h2>
          <div className="flex-1 h-px bg-border" />
        </div>

        <div className="space-y-8">
          {grouped.map(({ category, items }) => (
            <div key={category}>
              <h3 className="text-xs font-semibold text-muted uppercase tracking-wider mb-3">{category}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {items.map(calc => (
                  <QuickCard
                    key={calc.key}
                    icon={<calc.icon />}
                    title={calc.title}
                    description={calc.description}
                    onClick={() => handleCardClick(calc)}
                    color={colors[calc.colorKey]}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}

function QuickCard({ icon, title, description, onClick, color }) {
  return (
    <GlassCard hover className="group" onClick={onClick}>
      <div className="flex items-start gap-4">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-transform duration-200 group-hover:scale-110"
          style={{ backgroundColor: `${color}22`, color }}
        >
          {icon}
        </div>
        <div>
          <h4 className="text-sm font-semibold text-heading mb-1">{title}</h4>
          <p className="text-xs text-muted leading-relaxed">{description}</p>
        </div>
      </div>
    </GlassCard>
  )
}

// ── Icons ──────────────────────────────────────────────────────────

function GridIcon() {
  return (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
    </svg>
  )
}

function ShieldIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m0-10.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.75c0 5.592 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.57-.598-3.75h-.152c-3.196 0-6.1-1.249-8.25-3.286z" />
    </svg>
  )
}

function TargetIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
}

function ChartIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
    </svg>
  )
}

function MapIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z" />
    </svg>
  )
}

function LoanIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  )
}

function CompareIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
    </svg>
  )
}

function DebtIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
    </svg>
  )
}

function SwapIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
    </svg>
  )
}

function CarIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0H6.375c-.621 0-1.125-.504-1.125-1.125V14.25m17.25 0V6.375c0-.621-.504-1.125-1.125-1.125H4.125C3.504 5.25 3 5.754 3 6.375v7.875" />
    </svg>
  )
}

function HomeIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
    </svg>
  )
}

function RentBuyIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
    </svg>
  )
}

function HeartIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
    </svg>
  )
}

function ClockIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
}

function CalcIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  )
}

function TrendUpIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
    </svg>
  )
}

function FireIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.048 8.287 8.287 0 009 9.6a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 18a3.75 3.75 0 00.495-7.467 5.99 5.99 0 00-1.925 3.546 5.974 5.974 0 01-2.133-1.001A3.75 3.75 0 0012 18z" />
    </svg>
  )
}

function BriefcaseIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0" />
    </svg>
  )
}

function TrendingIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" />
    </svg>
  )
}

function GiftIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
    </svg>
  )
}

function DollarIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
}

function ClipboardIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15a2.25 2.25 0 012.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25" />
    </svg>
  )
}

function CreepIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" />
    </svg>
  )
}

function SSIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
    </svg>
  )
}
